import { useQuery } from "@tanstack/react-query";

/**
 * Returns whether the current session user has admin access.
 *
 * Calls GET /api/web/v2/admin/access. A 403 response means "not an admin"
 * (the endpoint only 403s for authenticated non-admins). A 401 means the
 * session is gone, which AuthGuard handles independently.
 *
 * staleTime: Infinity — admin status doesn't change during a session.
 * The cache is cleared on logout/session-expiry via queryClient.clear().
 */
export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const query = useQuery({
    queryKey: ["admin-access"],
    queryFn: async () => {
      const r = await fetch(`${import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com"}/api/web/v2/admin/access`, {
        credentials: "include",
      });
      if (r.status === 403 || r.status === 401) return false;
      if (!r.ok) throw new Error(`admin/access ${r.status}`);
      return true;
    },
    staleTime: Infinity,
    retry: false,
  });

  return {
    isAdmin: query.data === true,
    isLoading: query.isLoading,
  };
}
